import React, { useState, useEffect } from 'react';
import { IconUpload, IconX, IconAlertCircle, IconPaperclip } from '@tabler/icons-react';

const AddLeaveModal = ({
    isOpen,
    onClose,
    leaveTypes,
    selectedLeaveType,
    setSelectedLeaveType,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    totalDays,
    leaveReason,
    setLeaveReason,
    onSubmit,
    isSubmitting,
    employees,
    selectedEmployee,
    setSelectedEmployee
}) => {
    const [attachmentFiles, setAttachmentFiles] = useState([]);

    // Reset files when modal opens/closes
    useEffect(() => {
        if (!isOpen) setAttachmentFiles([]);
    }, [isOpen]);

    const selectedTypeObj = leaveTypes.find(
        t => (t.id || t.value)?.toString() === selectedLeaveType?.toString()
    );
    const isSickLeave = selectedTypeObj?.name?.toLowerCase().includes('sick');
    const attachmentRequired = isSickLeave && totalDays > 2;
    // Show upload field for any leave type once selected
    const showUpload = !!selectedLeaveType;

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setAttachmentFiles(prev => [...prev, ...newFiles]);
        e.target.value = '';
    };

    const removeFile = (index) => {
        setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
    };

    if (!isOpen) return null;

    return (
        <>
            <div className={`fixed p-15 w-full max-w-[600px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[6]`}>
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-light'>
                    <h5 className="text-[16px]/[19.2px] font-medium md:px-10 px-[7px] py-10 mb-20 text-center border-b border-border">
                        Apply for Leave
                    </h5>
                    <div className='my-10 md:px-10 px-[7px] max-h-[60vh] overflow-auto cus-scrollbar'>
                        <div className='grid grid-cols-12 gap-20'>

                            {/* Employee selection removed as per user request - always applies to self */}

                            <div className='form-group col-span-12'>
                                <label className='text-sm mb-2 block'>Leave Type *</label>
                                <select
                                    name="selectLeaveType"
                                    id="selectLeaveType"
                                    value={selectedLeaveType}
                                    onChange={(e) => setSelectedLeaveType(e.target.value)}
                                    className="w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary"
                                >
                                    <option value="">-- Select Leave Type --</option>
                                    {leaveTypes.map(type => (
                                        <option key={type.id || type.value} value={type.id || type.value}>
                                            {type.name || type.label}
                                            {type.balance !== undefined ? ` (${type.balance} days available)` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedLeaveType && (
                                <div className='col-span-12'>
                                    <div className='bg-bg-light border border-border rounded p-3 mb-2'>
                                        <p className='text-sm'>
                                            <span className='font-medium'>Max Days: </span>
                                            {(() => {
                                                const type = leaveTypes.find(t => (t.id || t.value).toString() === selectedLeaveType.toString());
                                                return <span className="ml-2 font-bold">{type?.max_days_per_year || '-'}</span>
                                            })()}
                                            <span className='ml-4 font-medium'>Type: </span>
                                            {(() => {
                                                const type = leaveTypes.find(t => (t.id || t.value).toString() === selectedLeaveType.toString());
                                                return <span className="ml-2 font-bold">{type?.name || type?.label}</span>
                                            })()}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className='form-group flex md:col-span-6 col-span-12'>
                                <div className='w-full'>
                                    <label className='text-sm mb-2 block'>From Date *</label>
                                    <input
                                        type='date'
                                        id='fromDate'
                                        name='fromDate'
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        className='grow w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary'
                                    />
                                </div>
                            </div>

                            <div className='form-group flex md:col-span-6 col-span-12'>
                                <div className='w-full'>
                                    <label className='text-sm mb-2 block'>To Date *</label>
                                    <input
                                        type='date'
                                        id='toDate'
                                        name='toDate'
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        min={fromDate}
                                        className='grow w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary'
                                    />
                                </div>
                            </div>

                            {totalDays > 0 && (
                                <div className='col-span-12'>
                                    <div className=' bg-opacity-10 border border-blue rounded p-3'>
                                        <p className='text-sm'>
                                            <span className='font-medium'>Total Days Requested:</span>
                                            <span className='ml-2 font-bold text-blue'>{totalDays} days</span>
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className='form-group flex col-span-12'>
                                <div className='w-full'>
                                    <label className='text-sm mb-2 block'>Leave Reason *</label>
                                    <textarea
                                        rows={4}
                                        placeholder='Enter reason for leave request...'
                                        value={leaveReason}
                                        onChange={(e) => setLeaveReason(e.target.value)}
                                        className='w-full bg-bg-light border border-border px-12 py-8 rounded-md text-sm focus:outline-none focus:border-primary resize-none'
                                    />
                                </div>
                            </div>

                            {/* Attachment section */}
                            {showUpload && (
                                <div className='col-span-12'>
                                    {attachmentRequired && (
                                        <div className='flex items-start gap-8 bg-warning/10 border border-warning/40 rounded-md px-12 py-10 mb-10'>
                                            <IconAlertCircle size={16} className='text-warning mt-[2px] flex-shrink-0' />
                                            <p className='text-xs text-warning'>
                                                A supporting document (e.g. medical certificate) is <strong>required</strong> for Sick Leave requests exceeding 2 days.
                                            </p>
                                        </div>
                                    )}

                                    <label className='text-sm mb-2 block'>
                                        Supporting Documents {attachmentRequired ? <span className='text-danger'>*</span> : <span className='text-text-secondary'>(optional)</span>}
                                    </label>

                                    <label className='flex items-center gap-8 w-full cursor-pointer border border-dashed border-border hover:border-primary rounded-md px-12 py-10 bg-bg-light transition-colors'>
                                        <IconUpload size={16} className='text-text-secondary flex-shrink-0' />
                                        <span className='text-sm text-text-secondary'>Click to upload files</span>
                                        <input
                                            type='file'
                                            multiple
                                            accept='.pdf,.jpg,.jpeg,.png,.doc,.docx'
                                            onChange={handleFileChange}
                                            className='hidden'
                                        />
                                    </label>

                                    {attachmentFiles.length > 0 && (
                                        <div className='mt-8 flex flex-col gap-6'>
                                            {attachmentFiles.map((file, idx) => (
                                                <div key={idx} className='flex items-center justify-between bg-bg-light border border-border rounded-md px-10 py-6'>
                                                    <div className='flex items-center gap-8 min-w-0'>
                                                        <IconPaperclip size={14} className='text-text-secondary flex-shrink-0' />
                                                        <span className='text-xs text-text-primary truncate'>{file.name}</span>
                                                        <span className='text-xs text-text-secondary flex-shrink-0'>
                                                            ({(file.size / 1024).toFixed(0)} KB)
                                                        </span>
                                                    </div>
                                                    <button
                                                        type='button'
                                                        onClick={() => removeFile(idx)}
                                                        className='ml-8 text-text-secondary hover:text-danger flex-shrink-0'
                                                    >
                                                        <IconX size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className='md:px-10 px-[7px] flex gap-10 justify-end py-10'>
                        <button onClick={onClose} className='btn btn-outline-secondary'>
                            Cancel
                        </button>
                        <button
                            onClick={() => onSubmit(attachmentFiles)}
                            className='btn btn-primary'
                            disabled={isSubmitting || (attachmentRequired && attachmentFiles.length === 0)}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Leave Request'}
                        </button>
                    </div>
                </div>
            </div>
            <div onClick={onClose} className='fixed z-[5] w-full h-full left-0 top-0 bg-bg-transparent backdrop-blur-[5px]'></div>
        </>
    );
};

export default AddLeaveModal;
